import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

import { Observable, startWith, Subscription, takeUntil } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DefaultIcon } from '@summit/core';
import { SummitIdObject } from '@summit/models';
import { Checkbox } from 'primeng/checkbox';
import { SummitControl } from '../../models';
import { SFormErrorInputComponent } from '../s-form-error-input';

@Component({
  selector: 's-form-input-checkbox',
  templateUrl: './s-form-input-checkbox.component.html',
  styleUrls: ['./s-form-input-checkbox.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, SFormErrorInputComponent, Checkbox, AsyncPipe, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputCheckboxComponent<T extends SummitIdObject>
  extends SummitControl<T>
  implements OnInit, OnChanges
{
  /* if there is a nullable then binary false */
  @Input() disabled$: Observable<boolean>;
  @Input() displayHint$: Observable<string>;
  @Input() allowNull$: Observable<boolean>;
  lclAllowNull = false;
  isNull = false;
  triStateValue: boolean | null;
  disableSubscription: Subscription;

  defaultIcon = DefaultIcon;

  override ngOnInit() {
    super.ngOnInit();
    const fieldName = this.fieldName;
    this.allowNull$?.pipe(takeUntil(this.disposing$)).subscribe((allowLocal) => {
      //console.log('setting lcl allow null', allowLocal);
      this.lclAllowNull = allowLocal;
      this.cd.detectChanges();
    });

    //console.log(`FormInputCheckboxComponent.ngOnInit() ${this.fieldName}`, this.form.controls[this.fieldName]);
    if (this.nullableLabel) this.lclAllowNull = true;

    if (this.fieldName && this.form.controls[this.fieldName]) {
      const control = this.form.controls[this.fieldName];
      control.valueChanges.pipe(startWith(control.value), takeUntil(this.disposing$)).subscribe((value) => {
        //console.log(`${this.fieldName.toString()}: watching value changes '${value}'`, value);
        if (value === undefined && !this.lclAllowNull) {
          control.patchValue(false);
          value = false;
          //this.cd.detectChanges();
        }
        if (value === undefined || value === null) {
          this.isNull = true;
          this.triStateValue = null;
        } else {
          this.isNull = false;
          this.triStateValue = value;
        }
        this.cd.detectChanges();
      });
    }

    if (this.disabled$) {
      this.disableSubscription = this.disabled$.pipe(takeUntil(this.disposing$)).subscribe((disabled) => {
        if (!this.fieldName) return;
        const control = this.form.controls[this.fieldName];
        if (!control) return;
        if (disabled) {
          control.disable();
        } else {
          control.enable();
        }
      });
    }
    // console.log(`id = ${this.htmlId}`);
  }

  override ngOnChanges(_changes: SimpleChanges): void {
    const fieldName = this.fieldName;
    if (this.disabled$) {
      if (this.disableSubscription) {
        this.disableSubscription.unsubscribe();
      }
      this.disableSubscription = this.disabled$.pipe(takeUntil(this.disposing$)).subscribe((disabled) => {
        if (!this.fieldName) return;
        const control = this.form.controls[this.fieldName];
        if (!control) return;
        if (disabled) {
          control.disable();
        } else {
          control.enable();
        }
      });
    }
    super.ngOnChanges(_changes);
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }
  // stopPropagation(event: Event) {

  toggle() {
    if (!this.fieldName) return;
    const control = this.form.controls[this.fieldName];
    if (!control) return;
    if (control.disabled) return;
    // console.log('toggle', this.lclAllowNull, control.value, this.isNull);
    if (this.lclAllowNull && control.value === false) {
      // console.log('setting null', control.value);
      control.patchValue(null);
      this.isNull = true;
      this.triStateValue = null;
    } else if (control.value === true) {
      // console.log('setting false', control.value);
      control.patchValue(false);
      this.isNull = false;
      this.triStateValue = false;
    } else {
      // console.log('setting true', control.value);
      control.patchValue(true);
      this.isNull = false;
      this.triStateValue = true;
    }
    this.cd.detectChanges();
  }
}
